import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { quoteAPI, promotionsAPI, quotePageAPI, quoteStepsAPI, quoteContentAPI, pdfGenerationAPI } from '../services/api';
import { LoadingScreen, ErrorScreen } from '../hooks/useApiLoading';
import { 
  Calculator,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Download,
  Send,
  Zap,
  Clock,
  DollarSign,
  Users,
  Smartphone,
  Globe,
  ShoppingCart,
  Palette,
  Code,
  Database,
  Shield,
  Star,
  Cloud,
  MessageCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { gsap } from 'gsap';
import { useNotificationHook } from '../hooks/useNotificationHook';

const Quote = () => {
  const { notify } = useNotificationHook();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    projectType: '',
    features: [],
    timeline: '',
    budget: '',
    complexity: '',
    contact: {
      name: '',
      email: '',
      phone: '',
      company: ''
    }
  });
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState(null);

  // Quote page header state
  const [pageHeader, setPageHeader] = useState({
    page_title: 'Get Your Project Quote',
    page_description: 'AI-powered estimation tool to get instant quotes for your project',
    calculator_icon: 'Calculator'
  });

  // CTA section state
  const [cta, setCta] = useState({ 
    cta_title: 'Ready to Start Your Project?', 
    cta_description: 'Let\'s discuss your project requirements and create something amazing together',
    cta_button1_text: 'Start Your Project',
    cta_button1_link: '/contact',
    cta_button2_text: 'Get Free Quote',
    cta_button2_link: '/quote'
  });

  // Banner promotions state
  const [promotions, setPromotions] = useState([]);
  const [topBannerPromotions, setTopBannerPromotions] = useState([]);
  const [topBannerIndex, setTopBannerIndex] = useState(0);

  // Quote steps state
  const [quoteSteps, setQuoteSteps] = useState([]);
  
  // Dynamic content state
  const [projectTypes, setProjectTypes] = useState([]);
  const [features, setFeatures] = useState({});
  const [timelines, setTimelines] = useState([]);
  const [complexityLevels, setComplexityLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize form data to prevent unnecessary re-renders
  const memoizedFormData = useMemo(() => formData, [formData]);

  // Fetch quote page data
  const fetchQuoteData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await quotePageAPI.getContent();
      if (response?.data?.success) {
        const data = response.data.data;
        
        // Set page header data
        setPageHeader({
          page_title: data.page_title || 'Get Your Project Quote',
          page_description: data.page_description || 'AI-powered estimation tool to get instant quotes for your project',
          calculator_icon: data.calculator_icon || 'Calculator'
        });
        
        // Set CTA data
        setCta({
          cta_title: data.cta_title || 'Ready to Start Your Project?',
          cta_description: data.cta_description || 'Let\'s discuss your project requirements and create something amazing together',
          cta_button1_text: data.cta_button1_text || 'Start Your Project',
          cta_button1_link: data.cta_button1_link || '/contact',
          cta_button2_text: data.cta_button2_text || 'Get Free Quote',
          cta_button2_link: data.cta_button2_link || '/quote'
        });
      } else {
        setError('Failed to load quote page content');
      }
    } catch (err) {
      console.error('Error fetching quote data:', err);
      setError('Failed to load quote page content');
    } finally {
      setLoading(false);
    }
  };

  // Fetch quote steps
  const fetchQuoteSteps = async () => {
    try {
      console.log('🔍 Fetching quote steps for website...');
      const response = await quoteStepsAPI.getAll();
      console.log('✅ Quote steps response:', response);
      
      if (response?.data?.success) {
        console.log('✅ Setting quote steps:', response.data.data);
        setQuoteSteps(response.data.data);
      } else {
        console.warn('⚠️ No quote steps data in response');
      }
    } catch (err) {
      console.error('❌ Error fetching quote steps:', err);
      // Keep default steps if API fails
    }
  };

  // Fetch project types
  const fetchProjectTypes = async () => {
    try {
      console.log('🔍 Fetching project types...');
      const response = await quoteContentAPI.getProjectTypes();
      console.log('✅ Project types response:', response);
      
      if (response?.data?.success) {
        console.log('✅ Setting project types:', response.data.data);
        setProjectTypes(response.data.data);
      } else {
        console.warn('⚠️ No project types data in response');
      }
    } catch (err) {
      console.error('❌ Error fetching project types:', err);
    }
  };

  // Fetch features for a project type
  const fetchFeatures = async (projectTypeId) => {
    try {
      console.log('🔍 Fetching features for project type:', projectTypeId);
      const response = await quoteContentAPI.getFeatures(projectTypeId);
      console.log('✅ Features response:', response);
      
      if (response?.data?.success) {
        console.log('✅ Setting features for project type:', projectTypeId, response.data.data);
        setFeatures(prev => ({
          ...prev,
          [projectTypeId]: response.data.data
        }));
      } else {
        console.warn('⚠️ No features data in response');
      }
    } catch (err) {
      console.error('❌ Error fetching features:', err);
    }
  };

  // Fetch timelines
  const fetchTimelines = async () => {
    try {
      console.log('🔍 Fetching timelines...');
      const response = await quoteContentAPI.getTimelines();
      console.log('✅ Timelines response:', response);
      
      if (response?.data?.success) {
        console.log('✅ Setting timelines:', response.data.data);
        setTimelines(response.data.data);
      } else {
        console.warn('⚠️ No timelines data in response');
      }
    } catch (err) {
      console.error('❌ Error fetching timelines:', err);
    }
  };

  // Fetch complexity levels
  const fetchComplexityLevels = async () => {
    try {
      console.log('🔍 Fetching complexity levels...');
      const response = await quoteContentAPI.getComplexityLevels();
      console.log('✅ Complexity levels response:', response);
      
      if (response?.data?.success) {
        console.log('✅ Setting complexity levels:', response.data.data);
        setComplexityLevels(response.data.data);
      } else {
        console.warn('⚠️ No complexity levels data in response');
      }
    } catch (err) {
      console.error('❌ Error fetching complexity levels:', err);
    }
  };

  useEffect(() => {
    fetchQuoteData(); // Fetch quote page data on mount
    fetchQuoteSteps(); // Fetch quote steps on mount
    fetchProjectTypes(); // Fetch project types on mount
    fetchTimelines(); // Fetch timelines on mount
    fetchComplexityLevels(); // Fetch complexity levels on mount
  }, []);

  useEffect(() => {
    const quoteContent = document.querySelector('.quote-content');
    if (quoteContent) {
      gsap.set('.quote-content', { opacity: 0, y: 30 });
      gsap.to('.quote-content', { duration: 0.6, opacity: 1, y: 0, ease: 'power3.out' });
    }
  }, [currentStep]);

  // Icon mapping for calculator icon
  const getIconComponent = (iconName) => {
    const icons = {
      'Calculator': Calculator,
      'Code': Code,
      'Smartphone': Smartphone,
      'Globe': Globe,
      'ShoppingCart': ShoppingCart,
      'Palette': Palette,
      'Database': Database,
      'Cloud': Cloud,
      'Shield': Shield,
      'Zap': Zap,
      'Star': Star,
      'CheckCircle': CheckCircle,
      'ArrowRight': ArrowRight,
      'Download': Download,
      'Send': Send,
      'Clock': Clock,
      'DollarSign': DollarSign,
      'Users': Users,
      'MessageCircle': MessageCircle
    };
    return icons[iconName] || Calculator;
  };

  const defaultProjectTypes = [
    { id: 'website', name: 'Website', icon: Globe, basePrice: 50000, description: 'Business website or portfolio' },
    { id: 'webapp', name: 'Web App', icon: Code, basePrice: 150000, description: 'Custom web application' },
    { id: 'mobile', name: 'Mobile App', icon: Smartphone, basePrice: 200000, description: 'iOS/Android application' },
    { id: 'ecommerce', name: 'E-commerce', icon: ShoppingCart, basePrice: 100000, description: 'Online store platform' },
    { id: 'design', name: 'UI/UX Design', icon: Palette, basePrice: 30000, description: 'Design services only' }
  ];

  const defaultFeatures = {
    website: [
      { id: 'responsive', name: 'Responsive Design', price: 0, included: true },
      { id: 'cms', name: 'Content Management', price: 15000 },
      { id: 'seo', name: 'SEO Optimization', price: 10000 },
      { id: 'analytics', name: 'Analytics Integration', price: 5000 },
      { id: 'multilang', name: 'Multi-language', price: 20000 },
      { id: 'booking', name: 'Booking System', price: 25000 }
    ],
    webapp: [
      { id: 'auth', name: 'User Authentication', price: 20000 },
      { id: 'dashboard', name: 'Admin Dashboard', price: 30000 },
      { id: 'api', name: 'API Integration', price: 25000 },
      { id: 'realtime', name: 'Real-time Features', price: 35000 },
      { id: 'payment', name: 'Payment Gateway', price: 20000 },
      { id: 'notifications', name: 'Push Notifications', price: 15000 }
    ],
    mobile: [
      { id: 'crossplatform', name: 'Cross-platform', price: 50000 },
      { id: 'offline', name: 'Offline Support', price: 30000 },
      { id: 'push', name: 'Push Notifications', price: 15000 },
      { id: 'location', name: 'Location Services', price: 20000 },
      { id: 'camera', name: 'Camera Integration', price: 15000 },
      { id: 'social', name: 'Social Login', price: 10000 }
    ],
    ecommerce: [
      { id: 'inventory', name: 'Inventory Management', price: 25000 },
      { id: 'multivendor', name: 'Multi-vendor Support', price: 50000 },
      { id: 'shipping', name: 'Shipping Integration', price: 20000 },
      { id: 'reviews', name: 'Reviews & Ratings', price: 10000 },
      { id: 'coupons', name: 'Discount System', price: 15000 },
      { id: 'reports', name: 'Analytics Reports', price: 20000 }
    ],
    design: [
      { id: 'wireframes', name: 'Wireframes', price: 0, included: true },
      { id: 'prototype', name: 'Interactive Prototype', price: 15000 },
      { id: 'branding', name: 'Brand Identity', price: 25000 },
      { id: 'illustrations', name: 'Custom Illustrations', price: 20000 },
      { id: 'animations', name: 'Micro-animations', price: 15000 },
      { id: 'guidelines', name: 'Design Guidelines', price: 10000 }
    ]
  };

  const defaultTimelines = [
    { id: 'rush', name: 'Rush (1-2 weeks)', multiplier: 1.5, description: 'Expedited delivery' },
    { id: 'standard', name: 'Standard (1-2 months)', multiplier: 1, description: 'Regular timeline' },
    { id: 'extended', name: 'Extended (3+ months)', multiplier: 0.9, description: 'Flexible timeline' }
  ];

  const defaultComplexities = [
    { id: 'simple', name: 'Simple', multiplier: 1, description: 'Basic functionality' },
    { id: 'medium', name: 'Medium', multiplier: 1.3, description: 'Moderate complexity' },
    { id: 'complex', name: 'Complex', multiplier: 1.7, description: 'Advanced features' }
  ];

  useEffect(() => {
    calculateEstimate();
  }, [formData]);

  const calculateEstimate = () => {
    // Use dynamic data if available, otherwise fall back to defaults
    const currentProjectTypes = projectTypes.length > 0 ? projectTypes : defaultProjectTypes;
    const currentFeatures = Object.keys(features).length > 0 ? features : defaultFeatures;
    const currentTimelines = timelines.length > 0 ? timelines : defaultTimelines;
    const currentComplexities = complexityLevels.length > 0 ? complexityLevels : defaultComplexities;

    const projectType = currentProjectTypes.find(p => p.id === formData.projectType);
    if (!projectType) return;

    let basePrice = parseFloat(projectType.base_price || projectType.basePrice);
    let featurePrice = 0;

    const projectFeatures = currentFeatures[formData.projectType] || [];
    formData.features.forEach(featureId => {
      const feature = projectFeatures.find(f => f.id === featureId);
      if (feature) featurePrice += parseFloat(feature.price);
    });

    const timeline = currentTimelines.find(t => t.id === formData.timeline);
    const complexity = currentComplexities.find(c => c.id === formData.complexity);

    const timelineMultiplier = timeline ? parseFloat(timeline.multiplier) : 1;
    const complexityMultiplier = complexity ? parseFloat(complexity.multiplier) : 1;

    const totalCost = (basePrice + featurePrice) * timelineMultiplier * complexityMultiplier;
    setEstimatedCost(Math.round(totalCost));

    // Calculate estimated time
    const baseWeeks = {
      website: 2,
      webapp: 8,
      mobile: 12,
      ecommerce: 6,
      design: 3
    };

    const baseTime = baseWeeks[formData.projectType] || 4;
    const featureWeeks = Math.ceil(formData.features.length * 0.5);
    const complexityWeeks = complexity ? Math.ceil(baseTime * (complexity.multiplier - 1)) : 0;
    
    const totalWeeks = baseTime + featureWeeks + complexityWeeks;
    setEstimatedTime(totalWeeks > 1 ? `${totalWeeks} weeks` : `${totalWeeks} week`);
  };

  const handleProjectTypeSelect = (typeId) => {
    setFormData(prev => ({
      ...prev,
      projectType: typeId,
      features: []
    }));
    
    // Fetch features for the selected project type if not already loaded
    if (!features[typeId]) {
      fetchFeatures(typeId);
    }
    
    calculateEstimate();
  };

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // Handle download quote
  const handleDownloadQuote = async () => {
    if (currentStep < 5) {
      notify.warning('Please complete all steps before downloading the quote.', 'Complete Steps Required');
      return;
    }

    setDownloading(true);
    try {
      // Prepare quote data
      const quoteData = {
        projectType: formData.projectType,
        features: formData.features,
        timeline: formData.timeline,
        budget: formData.budget,
        complexity: formData.complexity,
        contact: formData.contact,
        estimatedCost,
        estimatedTime
      };

      // Generate PDF
      const response = await pdfGenerationAPI.generate(quoteData);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Quote-${formData.customerName || 'Customer'}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ PDF downloaded successfully');
        notify.success('Quote PDF downloaded successfully!', 'Download Complete');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error downloading PDF quote:', error);
      notify.error(`Failed to download PDF quote: ${error.message}. Please try again.`, 'Download Error');
    } finally {
      setDownloading(false);
    }
  };

  // Handle WhatsApp order
  const handleWhatsAppOrder = async () => {
    if (currentStep < 5) {
      notify.warning('Please complete all steps before ordering via WhatsApp.', 'Complete Steps Required');
      return;
    }

    try {
      // Get the WhatsApp number from current step data
      const currentStepData = quoteSteps.find(step => step.step_number === currentStep);
      const whatsappNumber = currentStepData?.step_5_whatsapp_number || '9515377622';
      
      // Prepare the message content
      const messageContent = `Hello! I would like to order a project with the following details:

Project Type: ${formData.projectType}
Features: ${formData.features.join(', ')}
Timeline: ${formData.timeline}
Budget: ${formData.budget}
Complexity: ${formData.complexity}
Estimated Cost: $${estimatedCost}
Estimated Time: ${estimatedTime}

Contact Information:
Name: ${formData.contact.name}
Email: ${formData.contact.email}
Phone: ${formData.contact.phone}
Company: ${formData.contact.company}

Please provide me with more information about the next steps.`;

      // Encode the message for WhatsApp URL
      const encodedMessage = encodeURIComponent(messageContent);
      
      // Create WhatsApp URL
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
      
      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');
      
      console.log('✅ WhatsApp order initiated');
    } catch (error) {
      console.error('❌ Error initiating WhatsApp order:', error);
      notify.error('Failed to open WhatsApp. Please try again.', 'WhatsApp Error');
    }
  };

  const handleSubmitQuote = async () => {
    try {
      // Clear any existing notifications
      setNotification(null);
      
      // Validate required contact fields
      if (!formData.contact.name || !formData.contact.email) {
        setNotification({
          type: 'error',
          message: 'Please fill in your name and email address before submitting the quote.'
        });
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact.email)) {
        setNotification({
          type: 'error',
          message: 'Please enter a valid email address.'
        });
        return;
      }

      // Get the actual project type name from the ID
      const selectedProjectType = projectTypes.find(p => p.id === formData.projectType);
      const selectedTimeline = timelines.find(t => t.id === formData.timeline);
      const selectedComplexity = complexityLevels.find(c => c.id === formData.complexity);
      
      if (!selectedProjectType || !selectedTimeline || !selectedComplexity) {
        setNotification({
          type: 'error',
          message: 'Please complete all steps before submitting the quote.'
        });
        return;
      }

      // Map project type names to expected backend values
      const projectTypeMapping = {
        'website': 'website',
        'web application': 'webapp',
        'web app': 'webapp',
        'mobile application': 'mobile',
        'mobile app': 'mobile',
        'e-commerce': 'ecommerce',
        'ecommerce': 'ecommerce',
        'design': 'design'
      };

      const timelineMapping = {
        'rush': 'rush',
        'standard': 'standard',
        'extended': 'extended'
      };

      const complexityMapping = {
        'simple': 'simple',
        'medium': 'medium',
        'complex': 'complex'
      };

      // Submit quote to backend
      const quoteData = {
        projectType: projectTypeMapping[selectedProjectType.name.toLowerCase()] || 'website',
        features: formData.features,
        timeline: timelineMapping[selectedTimeline.name.toLowerCase()] || 'standard',
        complexity: complexityMapping[selectedComplexity.name.toLowerCase()] || 'medium',
        contact: formData.contact
      };

      console.log('Submitting quote data:', quoteData);

      const response = await quoteAPI.generate(quoteData);
      
      if (response?.data?.success) {
        // Quote submitted successfully
        console.log('Quote submitted:', response.data.data);
        
        setNotification({
          type: 'success',
          message: 'Quote submitted successfully! We are contacting you soon.'
        });
        
        // Auto-hide success notification after 5 seconds
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      
      // Show more specific error messages
      let errorMessage = 'Failed to submit quote. Please try again.';
      
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join('\n');
        errorMessage = `Failed to submit quote:\n${errorMessages}`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setNotification({
        type: 'error',
        message: errorMessage
      });
    }
  };

    const renderStep = () => {
    // Get the current step from the database
    const currentStepData = quoteSteps.find(step => step.step_number === currentStep);
    const stepTitle = currentStepData?.title || 'Step ' + currentStep;
    const stepDescription = currentStepData?.description || '';
    
    // Only log in development mode and limit frequency
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Rendering step:', {
        currentStep,
        quoteSteps: quoteSteps.length,
        currentStepData,
        stepTitle,
        stepDescription
      });
    }
    
    switch (currentStep) {
      case 1:
        return (
          <div className="quote-content">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {stepTitle}
            </h2>
            {stepDescription && (
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {stepDescription}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectTypes.length > 0 ? (
                projectTypes.map((type) => {
                  const IconComponent = getIconComponent(type.icon);
                  return (
                    <button
                      key={type.id}
                      onClick={() => handleProjectTypeSelect(type.id)}
                      className={`card-3d p-6 rounded-2xl border-2 transition-all duration-300 text-left hover:scale-105 ${
                        formData.projectType === type.id
                          ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-lg'
                          : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md'
                      }`}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">{type.name}</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">{type.description}</p>
                      <p className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                        Starting from ₹{parseFloat(type.base_price).toLocaleString()}
                      </p>
                    </button>
                  );
                })
              ) : (
                // Fallback to default project types if none loaded
                defaultProjectTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => handleProjectTypeSelect(type.id)}
                      className={`p-6 rounded-lg border-2 transition-all duration-200 text-left ${
                        formData.projectType === type.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
                      }`}
                    >
                      <IconComponent className="w-8 h-8 text-primary-600 dark:text-primary-400 mb-3" />
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{type.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{type.description}</p>
                      <p className="text-primary-600 dark:text-primary-400 font-medium">
                        Starting from ₹{type.basePrice.toLocaleString()}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        );

      case 2:
        // Use dynamic features if available, otherwise fall back to defaults
        const currentFeatures = Object.keys(features).length > 0 ? features : defaultFeatures;
        const projectFeatures = currentFeatures[formData.projectType] || [];
        return (
          <div className="quote-content">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {stepTitle}
            </h2>
            {stepDescription && (
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {stepDescription}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projectFeatures.map((feature) => (
                <label
                  key={feature.id}
                  className={`card-3d p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 ${
                    formData.features.includes(feature.id) || feature.included
                      ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-lg'
                      : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        checked={formData.features.includes(feature.id) || feature.included}
                        onChange={() => !feature.included && handleFeatureToggle(feature.id)}
                        disabled={feature.included}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white text-lg">
                          {feature.name}
                        </span>
                        {feature.included && (
                          <span className="ml-3 text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full font-semibold">
                            Included
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                      {parseFloat(feature.price) === 0 ? 'Free' : `+₹${parseFloat(feature.price).toLocaleString()}`}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="quote-content">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {stepTitle}
            </h2>
            {stepDescription && (
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {stepDescription}
              </p>
            )}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {currentStepData?.step_3_timeline_subtitle || 'Timeline'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(timelines.length > 0 ? timelines : defaultTimelines).map((timeline) => (
                    <button
                      key={timeline.id}
                      onClick={() => handleInputChange('timeline', timeline.id)}
                      className={`card-3d p-6 rounded-2xl border-2 transition-all duration-300 text-left hover:scale-105 ${
                        formData.timeline === timeline.id
                          ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-lg'
                          : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md'
                      }`}
                    >
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">{timeline.name}</h4>
                      <p className="text-gray-600 dark:text-gray-400">{timeline.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {currentStepData?.step_3_complexity_subtitle || 'Complexity'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(complexityLevels.length > 0 ? complexityLevels : defaultComplexities).map((complexity) => (
                    <button
                      key={complexity.id}
                      onClick={() => handleInputChange('complexity', complexity.id)}
                      className={`card-3d p-6 rounded-2xl border-2 transition-all duration-300 text-left hover:scale-105 ${
                        formData.complexity === complexity.id
                          ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-lg'
                          : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md'
                      }`}
                    >
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3 text-lg">{complexity.name}</h4>
                      <p className="text-gray-600 dark:text-gray-400">{complexity.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="quote-content">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {stepTitle}
            </h2>
            {stepDescription && (
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {stepDescription}
              </p>
            )}
            
            {/* Contact Information Note */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Required Information:</strong> Please provide your name and email address. This information is required to submit your quote and will be used to contact you about your project.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.contact.name || ''}
                  onChange={(e) => handleInputChange('contact.name', e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-500"
                  placeholder="Your full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.contact.email || ''}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-500"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.contact.phone || ''}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-500"
                  placeholder="+91 9876543210"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.contact.company || ''}
                  onChange={(e) => handleInputChange('contact.company', e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white/80 dark:bg-gray-700/80 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 hover:border-blue-300 dark:hover:border-blue-500"
                  placeholder="Your company name"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="quote-content">
            <div className="text-center mb-12">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-400 dark:via-purple-400 dark:to-indigo-400 bg-clip-text text-transparent mb-6">
                {stepTitle}
              </h2>
              {stepDescription && (
                <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
                  {stepDescription}
                </p>
              )}
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {currentStepData?.step_5_subtitle || 'Based on your requirements, here\'s your personalized quote'}
              </p>
            </div>

            <div className="card-3d bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-10 text-white mb-10 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="text-center">
                  {(() => {
                    const PriceIcon = getIconComponent(currentStepData?.step_5_price_icon || 'DollarSign');
                    return (
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <PriceIcon className="w-8 h-8 text-white" />
                      </div>
                    );
                  })()}
                  <div className="text-5xl font-bold mb-3">₹{estimatedCost.toLocaleString()}</div>
                  <div className="text-white/90 text-lg">{currentStepData?.step_5_price_label || 'Estimated Cost'}</div>
                </div>
                <div className="text-center">
                  {(() => {
                    const TimeIcon = getIconComponent(currentStepData?.step_5_time_icon || 'Clock');
                    return (
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TimeIcon className="w-8 h-8 text-white" />
                      </div>
                    );
                  })()}
                  <div className="text-5xl font-bold mb-3">{estimatedTime}</div>
                  <div className="text-white/90 text-lg">{currentStepData?.step_5_time_label || 'Estimated Timeline'}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/20 dark:border-gray-700/50">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {currentStepData?.step_5_summary_title || 'Project Summary'}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                      {currentStepData?.step_5_project_type_label || 'Project Type'}:
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {projectTypes.find(p => p.id === formData.projectType)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                      {currentStepData?.step_5_features_label || 'Features'}:
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {formData.features.length} selected
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                      {currentStepData?.step_5_timeline_label || 'Timeline'}:
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {timelines.find(t => t.id === formData.timeline)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">
                      {currentStepData?.step_5_complexity_label || 'Complexity'}:
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {(complexityLevels.length > 0 ? complexityLevels : defaultComplexities).find(c => c.id === formData.complexity)?.name}
                    </span>
                  </div>
                </div>
              </div>

              <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl p-8 border border-white/20 dark:border-gray-700/50">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {currentStepData?.step_5_next_steps_title || 'Next Steps'}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-green-50/80 dark:bg-green-900/20 rounded-2xl">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300 font-semibold">
                      {currentStepData?.step_5_quote_generated_text || 'Quote generated'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-blue-50/80 dark:bg-blue-900/20 rounded-2xl">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    <span className="text-gray-700 dark:text-gray-300 font-semibold">
                      {currentStepData?.step_5_schedule_consultation_text || 'Schedule consultation'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-gray-50/80 dark:bg-gray-700/80 rounded-2xl">
                    <div className="w-8 h-8 border-2 border-gray-300 dark:border-gray-600 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-gray-400 dark:bg-gray-500 rounded-full"></div>
                    </div>
                    <span className="text-gray-600 dark:text-gray-400 font-semibold">
                      {currentStepData?.step_5_project_kickoff_text || 'Project kickoff'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mb-6">
              {/* WhatsApp Order Button */}
              <button 
                onClick={handleWhatsAppOrder}
                className="flex-1 card-3d bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-5 px-8 rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-105"
              >
                {(() => {
                  const WhatsAppIcon = getIconComponent(currentStepData?.step_5_whatsapp_button_icon || 'MessageCircle');
                  return <WhatsAppIcon className="w-6 h-6" />;
                })()}
                {currentStepData?.step_5_whatsapp_button_text || 'Order on WhatsApp'}
              </button>
            </div>

            <div className="flex gap-4">
              <button 
                onClick={handleDownloadQuote}
                disabled={downloading}
                className="flex-1 card-3d bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-5 px-8 rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    {(() => {
                      const DownloadIcon = getIconComponent(currentStepData?.step_5_download_button_icon || 'Download');
                      return <DownloadIcon className="w-6 h-6" />;
                    })()}
                    {currentStepData?.step_5_download_button_text || 'Download Quote'}
                  </>
                )}
              </button>
              <button 
                onClick={handleSubmitQuote}
                className="flex-1 card-3d bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-bold py-5 px-8 rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-105"
              >
                {(() => {
                  const SubmitIcon = getIconComponent(currentStepData?.step_5_submit_button_icon || 'Send');
                  return <SubmitIcon className="w-6 h-6" />;
                })()}
                {currentStepData?.step_5_submit_button_text || 'Submit Quote'}
              </button>
              <Link 
                to="/contact"
                className="flex-1 card-3d bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-5 px-8 rounded-2xl transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-105"
              >
                {(() => {
                  const StartProjectIcon = getIconComponent(currentStepData?.step_5_start_project_button_icon || 'Send');
                  return <StartProjectIcon className="w-6 h-6" />;
                })()}
                {currentStepData?.step_5_start_project_button_text || 'Start Project'}
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading screen when data is being fetched
  if (loading) {
    return <LoadingScreen message="Loading quote calculator..." />;
  }

  // Show error screen when there's an error
  if (error) {
    return <ErrorScreen error={error} onRetry={() => window.location.reload()} message="Unable to Load Quote Calculator" />;
  }

  // Don't render content if no data is available
  if (!pageHeader || !pageHeader.page_title) {
    return <LoadingScreen message="Loading content..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 dark:from-blue-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 dark:from-indigo-500/10 dark:to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-300/10 to-purple-300/10 dark:from-blue-400/5 dark:to-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 py-12">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Calculator className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 dark:from-blue-400 dark:via-purple-400 dark:to-indigo-400 bg-clip-text text-transparent mb-4">
              {pageHeader.page_title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2 max-w-3xl mx-auto">
              {pageHeader.page_description}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-2 relative z-10">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-2xl rounded-3xl border border-white/20 dark:border-gray-700/50 p-8 md:p-12">
              {renderStep()}
            </div>

            {/* Notifications Below Steps */}
            {notification && (
              <div className="mt-6">
                <div className={`rounded-lg p-4 border-l-4 ${
                  notification.type === 'success' 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-400 text-green-800 dark:text-green-200' 
                    : 'bg-red-50 dark:bg-red-900/20 border-red-400 text-red-800 dark:text-red-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {notification.type === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      )}
                      <p className="font-medium whitespace-pre-line">{notification.message}</p>
                    </div>
                    <button
                      onClick={() => setNotification(null)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center gap-3 px-8 py-4 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl hover:bg-gray-50/80 dark:hover:bg-gray-800/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 backdrop-blur-md font-semibold"
              >
                <ArrowLeft className="w-5 h-5" />
                Previous
              </button>
              
              <button
                onClick={nextStep}
                disabled={currentStep === (quoteSteps.length > 0 ? quoteSteps.length : 5) || (currentStep === 1 && !formData.projectType)}
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
              >
                Next
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Estimate Preview */}
      {estimatedCost > 0 && currentStep < 5 && (
        <div className="fixed bottom-6 right-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              ₹{estimatedCost.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Estimated Cost
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {cta.cta_title}
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto">
            {cta.cta_description}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to={cta.cta_button1_link} 
              className="card-3d bg-white text-blue-600 px-10 py-5 rounded-2xl font-bold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:scale-105"
            >
              {cta.cta_button1_text}
              <ArrowRight className="w-6 h-6" />
            </Link>
            <Link 
              to={cta.cta_button2_link} 
              className="card-3d border-2 border-white text-white px-10 py-5 rounded-2xl font-bold hover:bg-white hover:text-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              {cta.cta_button2_text}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
